import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  FileText,
  CreditCard,
  ArrowLeft,
  Send,
  MessageSquare,
  Home,
  Clock,
  Building2,
  BookOpen,
  DollarSign,
  Award,
  Info,
  Plane
} from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { bookingService, Booking } from '../services/bookingService';
import { messageService, Message } from '../services/messageService';
import BookingTracking from '../components/BookingTracking';
import PaymentModal from '../components/PaymentModal';
import PaymentMethodSelectModal from '../components/PaymentMethodSelectModal';
import RazorpayModal from '../components/RazorpayModal';
import React from 'react';

const TRAVEL_BOOKING_URL =
  (import.meta.env.VITE_TRAVEL_BOOKING_URL as string | undefined)?.trim() ||
  'https://travel.bookmyyogattc.com/';

const BookingDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isMobileMessagesOpen, setIsMobileMessagesOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRazorpayModal, setShowRazorpayModal] = useState(false);
  const [paymentAmountData, setPaymentAmountData] = useState<{ amount: number; currency: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top on initial mount and prevent scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Smooth scroll to top immediately
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const statusConfig = {
    pending: {
      label: 'Pending',
      color: 'bg-gradient-to-r from-ivory-100 to-ivory-200 text-ivory-800 border-ivory-300',
      icon: AlertCircle,
      accentColor: 'bg-primary'
    },
    approved: {
      label: 'Approved',
      color: 'bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200',
      icon: CheckCircle,
      accentColor: 'bg-emerald-500'
    },
    rejected: {
      label: 'Rejected',
      color: 'bg-gradient-to-r from-red-50 to-red-100 text-red-800 border-red-200',
      icon: XCircle,
      accentColor: 'bg-red-500'
    },
    cancelled: {
      label: 'Cancelled',
      color: 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 border-gray-200',
      icon: XCircle,
      accentColor: 'bg-gray-500'
    },
  };

  const substatusConfig = {
    'pending approval from school': {
      label: 'Pending School Approval',
      color: 'bg-gradient-to-r from-ivory-100 to-ivory-200 text-ivory-800 border-ivory-300',
      icon: AlertCircle
    },
    'pending payment': {
      label: 'Pending Payment',
      color: 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 border-amber-200',
      icon: CreditCard
    },
  };

  // Scroll to top when id changes
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Smooth scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchBooking();
    }
  }, [id]);

  useEffect(() => {
    setPaymentAmountData(null);
  }, [id]);

  useEffect(() => {
    if (booking?._id) {
      fetchMessages();
    }
  }, [booking?._id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchBooking = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const response = await bookingService.getBooking(id);
      if (response.success && response.data) {
        setBooking(response.data);
        // Scroll to top after booking loads - multiple attempts to ensure it works
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        });
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }, 100);
      } else {
        setError('Booking not found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load booking.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!booking?._id) return;
    setLoadingMessages(true);
    try {
      const response = await messageService.getMessages(undefined, undefined, booking._id);
      const fetchedMessages = response.data.messages || [];
      const visibleMessages = fetchedMessages.filter((m: Message) => m.status !== 'DELETED');
      setMessages(visibleMessages);

      const unreadIds = visibleMessages
        .filter((msg: Message) => !msg.isRead &&
          (typeof msg.receiverId === 'object' ? msg.receiverId._id : msg.receiverId) === user?.id)
        .map((msg: Message) => msg._id);

      if (unreadIds.length > 0) {
        await messageService.markAsRead(unreadIds);
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            unreadIds.includes(msg._id) ? { ...msg, isRead: true } : msg
          )
        );
      }
    } catch (err: any) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !booking?.schoolId || !booking?._id) return;
    
    // Check if messaging is allowed
    if (!isMessagingAllowed()) {
      setError(getMessagingDisabledMessage());
      return;
    }

    setSending(true);
    try {
      await messageService.sendMessage(booking.schoolId, newMessage.trim(), undefined, booking._id);
      setNewMessage('');
      await fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Check if messaging is allowed for this booking
  const isMessagingAllowed = () => {
    if (!booking) return false;
    const allowedStatuses = ['pending', 'approved'];
    return allowedStatuses.includes(booking.status);
  };

  const getMessagingDisabledMessage = () => {
    if (!booking) return '';
    if (booking.status === 'rejected') {
      return 'Messaging is disabled for rejected bookings.';
    }
    if (booking.status === 'cancelled') {
      return 'Messaging is disabled for cancelled bookings.';
    }
    return 'Messaging is not available for this booking.';
  };

  // Refresh booking data before opening payment modal to ensure we have latest status
  const handleOpenPaymentModal = async () => {
    if (!booking) return;

    // Use already loaded booking state to avoid extra API calls.
    try {
      // Check if payment is already completed
      if (booking.paymentCompletedAt) {
        setError('Payment has already been completed for this booking.');
        return;
      }

      // Backend requires: status === 'pending' AND substatus === 'pending payment'
      if (booking.status === 'pending' && booking.substatus === 'pending payment') {
        const amount = Number(booking.bookingAmount ?? 0);
        const currency = String(booking.bookingCurrency || booking.courseId_populated?.currency || 'USD').toUpperCase();
        if (amount <= 0) {
          setError('Booking amount is not initialized for this booking. Please contact support.');
          return;
        }
        setPaymentAmountData({ amount, currency });
        setShowPaymentMethodModal(true);
      } else if (booking.status === 'approved') {
        if (booking.substatus === 'pending payment') {
          setError(
            'This booking appears to be in an inconsistent state. ' +
            'Please refresh the page or contact support if payment was already completed.'
          );
        } else {
          setError('This booking has already been approved. Payment may have already been completed.');
        }
      } else if (booking.status === 'pending' && booking.substatus === 'pending approval from school') {
        setError('Please wait for school approval before making payment.');
      } else {
        setError(
          `Payment is not available. Current status: ${booking.status}, ` +
          `Substatus: ${booking.substatus || 'none'}. ` +
          `Please contact support if you believe this is an error.`
        );
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to initialize payment.');
    }
  };

  const handleCompletePayment = async () => {
    // Payment is completed via PayPal or Razorpay; close modals and refresh booking state
    setShowPaymentModal(false);
    setShowRazorpayModal(false);
    setError('');
    setPaymentSuccess(true);
    await fetchBooking();
    setTimeout(() => {
      setPaymentSuccess(false);
    }, 5000);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-ivory-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center mx-auto mb-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <p className="text-gray-600 font-medium">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-ivory-50">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <XCircle className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-5 font-semibold text-gray-900 mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">{error || 'The booking you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/bookings')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-primary-dark text-white rounded-2xl hover:shadow-xl transition-all font-semibold shadow-lg"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`Booking Details - ${booking.courseTitle || 'Course Booking'}`}
        description="View booking details and communicate with the school."
        url={`https://bookmyyogattc.com/bookings/${id}`}
      />
      <motion.div 
        className="pt-20 min-h-screen bg-fog"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Success Message Banner */}
          {paymentSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-4 font-semibold text-green-800 mb-1">
                    Payment Completed Successfully! 🎉
                  </h3>
                  <p className="text-2 text-green-700">
                    Your booking has been confirmed. The school will be notified and you'll receive a confirmation email shortly.
                  </p>
                  <a
                    href={TRAVEL_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border-2 border-green-300 text-green-800 font-semibold text-2 shadow-sm hover:bg-green-50 hover:border-green-400 transition-colors"
                  >
                    <Plane className="h-4 w-4 flex-shrink-0" />
                    Book your flight
                  </a>
                </div>
                <button
                  onClick={() => setPaymentSuccess(false)}
                  className="flex-shrink-0 p-2 hover:bg-green-100 rounded-full transition-colors"
                >
                  <XCircle className="h-5 w-5 text-green-600" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Header Section */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut', delay: 0.1 }}
          >
            <button
              onClick={() => navigate('/bookings')}
              className="inline-flex items-center gap-2 text-ivory-700 hover:text-primary transition-all mb-3 group p-2 rounded-xl hover:bg-white/50"
            >
              <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Back to Bookings</span>
            </button>

            {/* Hero Banner */}
            <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-xl p-4 mb-4 border border-primary/10 shadow-xl">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                      <BookOpen className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-5 font-semibold text-ivory-900 mb-0.5">{booking.courseTitle || 'Course Booking'}</h1>
                      <p className="text-1 text-ivory-600 mb-1">Booking ID: <span className="text-primary font-semibold">{booking.bookingReference || `BY-${booking._id.slice(-8).toUpperCase()}`}</span></p>
                    </div>
                  </div>
                </div>

                {/* Status Section */}
                <div className="lg:text-right">
                  <div className="flex flex-wrap gap-2 justify-start lg:justify-end mb-1">
                    <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-1 font-semibold border-2 shadow-lg ${statusConfig[booking.status]?.color || 'bg-gray-50 text-gray-700'}`}>
                      <span className={`w-2 h-2 rounded-full ${statusConfig[booking.status]?.accentColor || 'bg-gray-500'}`}></span>
                      {React.createElement(statusConfig[booking.status]?.icon || FileText, { className: "h-3 w-3" })}
                      <span>{statusConfig[booking.status]?.label || booking.status}</span>
                    </span>
                    {booking.substatus && booking.substatus !== null && booking.substatus !== undefined && booking.status !== 'rejected' && substatusConfig[booking.substatus] && (
                      <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-1 font-semibold border-2 shadow-lg ${substatusConfig[booking.substatus].color}`}>
                        {React.createElement(substatusConfig[booking.substatus].icon, { className: "h-3 w-3" })}
                        <span>{substatusConfig[booking.substatus].label}</span>
                      </span>
                    )}
                    {booking.substatus === 'pending payment' && booking.status === 'pending' && (
                      <button
                        onClick={handleOpenPaymentModal}
                        className="ml-2 px-6 py-2.5 text-white rounded-xl font-semibold hover:shadow-xl transition-all flex items-center gap-2 shadow-lg hover:scale-105"
                        style={{ backgroundColor: '#FF6B35' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF4D1A'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B35'}
                      >
                        <CreditCard className="h-4 w-4" />
                        Pay Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {booking.paymentCompletedAt && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="mb-4 rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-primary/5 p-4 shadow-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-sky-500 flex items-center justify-center shadow-md">
                  <Plane className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-3 font-semibold text-ivory-900">Book your flight</h3>
                  <p className="text-2 text-ivory-600 mt-0.5">
                    Traveling for your course? Open our travel partner in a new tab to search and book flights.
                  </p>
                </div>
              </div>
              <a
                href={TRAVEL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white font-semibold text-2 shadow-lg hover:opacity-95 transition-opacity whitespace-nowrap flex-shrink-0"
              >
                <Plane className="h-4 w-4" />
                Book your flight
              </a>
            </motion.div>
          )}

          {/* Main Content - 60/40 Split */}
          <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4">
            {/* Left Side - Booking Details (60%) */}
            <div className="space-y-4">
              {/* School Information Card */}
              {booking.schoolName && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut', delay: 0.15 }}
                  className="bg-white rounded-2xl shadow-xl border border-ivory-200 p-4 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center shadow-lg">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-2 font-semibold text-ivory-900">School Information</h3>
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                      </div>
                      <p className="text-3 font-semibold text-ivory-800 mb-1">{booking.schoolName}</p>
                      {booking.schoolId_populated?.location && (
                        <div className="flex items-center gap-2 text-ivory-600">
                          <MapPin className="h-4 w-4 text-primary" />
                          <span className="font-medium text-1 md:text-2">{booking.schoolId_populated.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Timeline Table */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut', delay: 0.2 }}
                className="bg-white rounded-2xl shadow-xl border border-ivory-200 p-3 md:p-4"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center shadow-lg">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-2 font-semibold text-ivory-900">Booking Timeline</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <tbody>
                      {booking.selectedMonth && (
                        <tr className="border-b border-dashed border-ivory-300">
                          <td className="py-3 px-2 md:px-4 w-1/3">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Selected Month</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 md:px-4">
                            <p className="text-2 font-semibold text-ivory-900">
                              {new Date(booking.selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </p>
                          </td>
                        </tr>
                      )}
                      {booking.accommodationType && (
                        <tr className="border-b border-dashed border-ivory-300">
                          <td className="py-3 px-2 md:px-4 w-1/3">
                            <div className="flex items-center gap-2">
                              <Home className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Accommodation</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 md:px-4">
                            <p className="text-2 font-semibold text-ivory-900">{booking.accommodationType}</p>
                          </td>
                        </tr>
                      )}
                      <tr className="border-b border-dashed border-ivory-300">
                        <td className="py-3 px-2 md:px-4 w-1/3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-1 font-semibold text-primary uppercase tracking-wide">Submitted</span>
                          </div>
                        </td>
                        <td className="py-3 px-2 md:px-4">
                          <p className="text-2 font-semibold text-ivory-900">{formatDate(booking.submittedAt)}</p>
                        </td>
                      </tr>
                      {booking.approvedAt && booking.status === 'approved' && (
                        <tr className="border-b border-dashed border-ivory-300">
                          <td className="py-3 px-2 md:px-4 w-1/3">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Approved</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 md:px-4">
                            <p className="text-2 font-semibold text-ivory-900">{formatDateTime(booking.approvedAt)}</p>
                          </td>
                        </tr>
                      )}
                      {booking.rejectedAt && booking.status === 'rejected' && (
                        <tr className="border-b border-dashed border-ivory-300">
                          <td className="py-3 px-2 md:px-4 w-1/3">
                            <div className="flex items-center gap-2">
                              <XCircle className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Rejected</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 md:px-4">
                            <p className="text-2 font-semibold text-ivory-900">{formatDateTime(booking.rejectedAt)}</p>
                          </td>
                        </tr>
                      )}
                      {booking.paymentCompletedAt && (
                        <tr>
                          <td className="py-3 px-2 md:px-4 w-1/3">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-4 w-4 text-primary flex-shrink-0" />
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Payment Completed</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 md:px-4">
                            <p className="text-2 font-semibold text-ivory-900">{formatDateTime(booking.paymentCompletedAt)}</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* Course Details Table */}
              {(booking.courseId_populated || booking.courseTitle) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut', delay: 0.3 }}
                  className="bg-white rounded-2xl shadow-xl border border-ivory-200 p-3 md:p-4"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center shadow-lg">
                      <BookOpen className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-2 font-semibold text-ivory-900">Course Details</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <tbody>
                        {booking.courseTitle && (
                          <tr className="border-b border-dashed border-ivory-300">
                            <td className="py-3 px-2 md:px-4 w-1/3">
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Course Name</span>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <p className="text-2 font-semibold text-ivory-900">{booking.courseTitle}</p>
                            </td>
                          </tr>
                        )}
                        {booking.courseId_populated?.type && (
                          <tr className="border-b border-dashed border-ivory-300">
                            <td className="py-3 px-2 md:px-4 w-1/3">
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Type</span>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <p className="text-2 font-semibold text-ivory-900">{booking.courseId_populated.type}</p>
                            </td>
                          </tr>
                        )}
                        {booking.courseId_populated?.duration && (
                          <tr className="border-b border-dashed border-ivory-300">
                            <td className="py-3 px-2 md:px-4 w-1/3">
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Duration</span>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <p className="text-2 font-semibold text-ivory-900">{booking.courseId_populated.duration} days</p>
                            </td>
                          </tr>
                        )}
                        {booking.courseId_populated?.difficulty && (
                          <tr className="border-b border-dashed border-ivory-300">
                            <td className="py-3 px-2 md:px-4 w-1/3">
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Difficulty</span>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <p className="text-2 font-semibold text-ivory-900">{booking.courseId_populated.difficulty}</p>
                            </td>
                          </tr>
                        )}
                        {booking.courseId_populated?.price !== undefined && booking.courseId_populated.price !== null && (
                          <tr className="border-b border-dashed border-ivory-300">
                            <td className="py-3 px-2 md:px-4 w-1/3">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-primary flex-shrink-0" />
                                <span className="text-1 font-semibold text-primary uppercase tracking-wide">Price</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <p className="text-2 font-semibold text-ivory-900">
                                {booking.courseId_populated.currency || '$'} {booking.courseId_populated.price.toLocaleString()}
                              </p>
                            </td>
                          </tr>
                        )}
                        {booking.accommodationType && (
                          <tr className="border-b border-dashed border-ivory-300">
                            <td className="py-3 px-2 md:px-4 w-1/3">
                              <div className="flex items-center gap-2">
                                <span className="text-1 font-semibold text-primary uppercase tracking-wide">Accommodation</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              {(() => {
                                const selectedRoom = booking.courseId_populated?.accommodation?.rooms?.find(
                                  room => room.type === booking.accommodationType
                                );

                                if (selectedRoom) {
                                  return (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <p className="text-2 font-semibold text-ivory-900">{selectedRoom.type}</p>
                                        {selectedRoom.price > 0 && (
                                          <p className="text-2 font-semibold text-primary">
                                            {booking.courseId_populated?.currency || '$'} {selectedRoom.price.toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                      {selectedRoom.description && (
                                        <p className="text-1 text-ivory-700">{selectedRoom.description}</p>
                                      )}
                                      {selectedRoom.amenities && selectedRoom.amenities.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                          {selectedRoom.amenities.map((amenity, idx) => (
                                            <span key={idx} className="px-2 py-1 bg-accent-light rounded text-1 font-semibold text-primary border border-accent-dark">
                                              {amenity}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return (
                                  <p className="text-2 font-semibold text-ivory-900">{booking.accommodationType}</p>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                        {booking.courseId_populated?.inclusions && booking.courseId_populated.inclusions.length > 0 && (
                          <tr>
                            <td className="py-3 px-2 md:px-4 w-1/3 align-top">
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Inclusions</span>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <ul className="space-y-1.5">
                                {booking.courseId_populated.inclusions.map((inclusion, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-1 text-ivory-700">
                                    <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="leading-relaxed">{inclusion}</span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                        {booking.courseId_populated?.requirements && booking.courseId_populated.requirements.length > 0 && (
                          <tr>
                            <td className="py-3 px-2 md:px-4 w-1/3 align-top">
                              <span className="text-1 font-semibold text-primary uppercase tracking-wide">Requirements</span>
                            </td>
                            <td className="py-3 px-2 md:px-4">
                              <ul className="space-y-1.5">
                                {booking.courseId_populated.requirements.map((requirement, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-1 text-ivory-700">
                                    <Info className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="leading-relaxed">{requirement}</span>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {/* Booking Tracking */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut', delay: 0.35 }}
                className="bg-white rounded-2xl shadow-xl border border-ivory-200 p-4"
              >
                <BookingTracking booking={booking} onPayNow={handleOpenPaymentModal} />
              </motion.div>

              {/* Initial Message */}
              {booking.message && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut', delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-xl border border-ivory-200 p-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center shadow-lg">
                      <MessageSquare className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-2 font-semibold text-ivory-900">Your Initial Message</h3>
                  </div>
                  <div className="bg-gradient-to-br from-ivory-100 to-ivory-200 rounded-lg p-3 border border-ivory-300">
                    <p className="text-ivory-700 leading-relaxed text-2">{booking.message}</p>
                  </div>
                </motion.div>
              )}

              {/* School Notes */}
              {booking.notes && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut', delay: 0.45 }}
                  className="bg-white rounded-2xl shadow-xl border border-ivory-200 p-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Award className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-2 font-semibold text-ivory-900">School Notes</h3>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 border border-amber-200">
                    <p className="text-ivory-700 leading-relaxed text-2">{booking.notes}</p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Side - Messages (Desktop Only) */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden lg:block lg:sticky lg:top-24 h-fit lg:max-h-[calc(100vh-8rem)]"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-ivory-200 flex flex-col h-full lg:h-[calc(100vh-8rem)] overflow-hidden">
                {/* Messages Header */}
                <div className="flex-shrink-0 bg-gradient-to-r from-primary via-primary to-primary-dark p-3">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm shadow-lg">
                      <MessageSquare className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-2">Messages</h3>
                      {booking.schoolName && (
                        <p className="text-1 text-white/90 font-medium">with {booking.schoolName}</p>
                      )}
                    </div>
                  </div>
    
                </div>

                {/* Messages List */}
                <div
                  ref={messagesContainerRef}
                  className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0 bg-gradient-to-b from-background to-ivory-50/50"
                >
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      </div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-ivory-100 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
                        <MessageSquare className="h-8 w-8 text-ivory-400" />
                      </div>
                      <p className="text-ivory-600 font-semibold mb-1 text-2">No messages yet</p>
                      <p className="text-1 text-ivory-500">Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((message) => {
                      const isSender = (typeof message.senderId === 'object' ? message.senderId._id : message.senderId) === user?.id;

                      return (
                        <div
                          key={message._id}
                          className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[75%] ${isSender ? 'order-2' : 'order-1'}`}>
                            {!isSender && (
                              <p className="text-1 font-semibold text-ivory-600 mb-1 px-2">
                                {typeof message.senderId === 'object'
                                  ? `${message.senderId.firstName} ${message.senderId.lastName}`
                                  : 'School'}
                              </p>
                            )}
                            <div className={`rounded-xl px-3 py-2 shadow-lg ${
                              isSender
                                ? 'bg-gradient-to-r from-primary to-primary-dark text-white rounded-br-md'
                                : 'bg-white text-ivory-900 border border-ivory-200 rounded-bl-md'
                            }`}>
                              <p className="text-1 leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
                              <p className={`text-1 mt-1.5 ${
                                isSender ? 'text-white/80' : 'text-ivory-500'
                              }`}>
                                {formatMessageTime(message.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="flex-shrink-0 border-t border-ivory-200 p-3 bg-gradient-to-r from-ivory-50 to-white">
                  {!isMessagingAllowed() ? (
                    <div className="bg-ivory-100 rounded-xl p-3 border border-ivory-300">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-ivory-600 flex-shrink-0" />
                        <p className="text-1 text-ivory-700 font-medium">{getMessagingDisabledMessage()}</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-3 py-2 text-1 border-2 border-ivory-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary bg-white text-ivory-900 placeholder-ivory-400 transition-all font-medium shadow-sm"
                        disabled={sending}
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="px-4 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-semibold shadow-lg hover:scale-105 text-1"
                      >
                        {sending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            <span className="hidden sm:inline">Send</span>
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Mobile Messages Floating Button */}
      <button
        onClick={() => setIsMobileMessagesOpen(true)}
        className="lg:hidden fixed bottom-6 right-5 z-[1200] w-14 h-14 rounded-full bg-primary text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Open messages"
      >
        <MessageSquare className="h-6 w-6" />
      </button>

      {/* Mobile Messages Modal */}
      <AnimatePresence>
        {isMobileMessagesOpen && (
          <motion.div
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-[1500] flex items-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileMessagesOpen(false)}
          >
            <motion.div
              className="w-full max-h-[85vh] bg-white rounded-t-3xl shadow-2xl flex flex-col overflow-hidden"
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-ivory-200">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  <span className="text-2 font-semibold text-ivory-900">Messages</span>
                </div>
                <button
                  onClick={() => setIsMobileMessagesOpen(false)}
                  className="text-1 font-semibold text-primary px-2 py-1 rounded-md hover:bg-ivory-100"
                >
                  Close
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="bg-white flex flex-col h-full">
                  {/* Reuse the same messages UI */}
                  <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0 bg-gradient-to-b from-background to-ivory-50/50"
                  >
                    {loadingMessages ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 bg-ivory-100 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
                          <MessageSquare className="h-8 w-8 text-ivory-400" />
                        </div>
                        <p className="text-ivory-600 font-semibold mb-1 text-2">No messages yet</p>
                        <p className="text-1 text-ivory-500">Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((message) => {
                        const isSender = (typeof message.senderId === 'object' ? message.senderId._id : message.senderId) === user?.id;

                        return (
                          <div
                            key={message._id}
                            className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[75%] ${isSender ? 'order-2' : 'order-1'}`}>
                              {!isSender && (
                                <p className="text-1 font-semibold text-ivory-600 mb-1 px-2">
                                  {typeof message.senderId === 'object'
                                    ? `${message.senderId.firstName} ${message.senderId.lastName}`
                                    : 'School'}
                                </p>
                              )}
                              <div className={`rounded-xl px-3 py-2 shadow-lg ${
                                isSender
                                  ? 'bg-gradient-to-r from-primary to-primary-dark text-white rounded-br-md'
                                  : 'bg-white text-ivory-900 border border-ivory-200 rounded-bl-md'
                              }`}>
                                <p className="text-1 leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
                                <p className={`text-1 mt-1.5 ${
                                  isSender ? 'text-white/80' : 'text-ivory-500'
                                }`}>
                                  {formatMessageTime(message.createdAt)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="border-t border-ivory-200 p-3 bg-gradient-to-r from-ivory-50 to-white">
                    {!isMessagingAllowed() ? (
                      <div className="bg-ivory-100 rounded-xl p-3 border border-ivory-300">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-ivory-600 flex-shrink-0" />
                          <p className="text-1 text-ivory-700 font-medium">{getMessagingDisabledMessage()}</p>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleSendMessage} className="flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type your message..."
                          className="flex-1 px-3 py-2 text-1 border-2 border-ivory-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary bg-white text-ivory-900 placeholder-ivory-400 transition-all font-medium shadow-sm"
                          disabled={sending}
                        />
                        <button
                          type="submit"
                          disabled={!newMessage.trim() || sending}
                          className="px-4 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-semibold shadow-lg hover:scale-105 text-1"
                        >
                          {sending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              <span className="hidden sm:inline">Send</span>
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Method Select Modal */}
      {booking && booking.substatus === 'pending payment' && booking.status === 'pending' && (
        <PaymentMethodSelectModal
          isOpen={showPaymentMethodModal}
          onClose={() => {
            setShowPaymentMethodModal(false);
            setPaymentAmountData(null);
          }}
          onSelectPayPal={() => {
            setShowPaymentMethodModal(false);
            setShowPaymentModal(true);
          }}
          onSelectRazorpay={() => {
            setShowPaymentMethodModal(false);
            setShowRazorpayModal(true);
          }}
        />
      )}

      {/* Payment Modal (PayPal) */}
      {booking && paymentAmountData && booking.substatus === 'pending payment' && booking.status === 'pending' && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentAmountData(null);
          }}
          onConfirm={handleCompletePayment}
          bookingId={booking._id}
          paymentLink={booking.paymentLink}
          amount={paymentAmountData.amount}
          currency={paymentAmountData.currency}
          courseName={booking.courseTitle || booking.courseId_populated?.name}
        />
      )}

      {/* Payment Modal (Razorpay) */}
      {booking && paymentAmountData && booking.substatus === 'pending payment' && booking.status === 'pending' && (
        <RazorpayModal
          isOpen={showRazorpayModal}
          onClose={() => {
            setShowRazorpayModal(false);
            setPaymentAmountData(null);
          }}
          onConfirm={handleCompletePayment}
          bookingId={booking._id}
          amount={paymentAmountData.amount}
          currency={paymentAmountData.currency}
          courseName={booking.courseTitle || booking.courseId_populated?.name}
        />
      )}
    </>
  );
};

export default BookingDetails;